import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { useMembership } from "@/context/MembershipContext";
import { CheckCircle, XCircle, Spinner, ArrowRight } from "@phosphor-icons/react";

export default function SubscribeSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const { refresh } = useMembership();
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setErr("Missing session id.");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      if (attempts.current > 10) {
        setState("error");
        setErr("Took too long to confirm payment. Check your email; your access will activate automatically.");
        return;
      }
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setErr(apiError(e));
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full id-card text-center py-12" data-testid="subscribe-success-card">
        {state === "polling" && (
          <>
            <Spinner size={48} className="mx-auto text-[#FF3333] animate-spin" />
            <h2 className="id-h2 mt-6 mb-2">Confirming payment</h2>
            <p className="text-zinc-400 font-manrope text-sm">Hold tight — this usually takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-[#FF3333]" />
            <h2 className="id-h2 mt-6 mb-2">You're in.</h2>
            <p className="text-zinc-300 font-manrope">
              Membership activated. {info?.membership?.days_remaining ? `${info.membership.days_remaining} day${info.membership.days_remaining === 1 ? "" : "s"} of access unlocked.` : ""}
            </p>
            <button onClick={() => navigate("/app", { replace: true })} className="id-btn-primary w-full mt-8" data-testid="continue-after-pay">
              Enter portal <ArrowRight size={14} weight="bold" />
            </button>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-zinc-500" />
            <h2 className="id-h2 mt-6 mb-2">Session expired</h2>
            <p className="text-zinc-400 font-manrope text-sm">Your checkout session expired before payment completed. No charge was made.</p>
            <Link to="/app/subscribe" className="id-btn-primary w-full mt-8" data-testid="retry-checkout">Try again</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-[#FF3333]" />
            <h2 className="id-h2 mt-6 mb-2">Hmm</h2>
            <p className="text-zinc-400 font-manrope text-sm">{err}</p>
            <Link to="/app/subscribe" className="id-btn-secondary w-full mt-8" data-testid="back-to-subscribe">Back to membership</Link>
          </>
        )}
      </div>
    </div>
  );
}
